import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { Lock, Eye, EyeOff, CheckCircle, AlertCircle, Loader2, Building } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function SetupAccount() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { loginWithToken } = useAuth();

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [accountInfo, setAccountInfo] = useState(null);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const tokenParam = searchParams.get('token');

  useEffect(() => {
    const validateToken = async () => {
      if (!tokenParam) {
        setError('Invalid setup link. No token provided.');
        setLoading(false);
        return;
      }

      try {
        const res = await fetch(`${API_URL}/api/auth/setup-account/${tokenParam}`);
        if (!res.ok) {
          const data = await res.json();
          throw new Error(data.detail || 'Invalid or expired setup link');
        }
        setAccountInfo(await res.json());
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    validateToken();
  }, [tokenParam]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    setSubmitting(true);
    try {
      // Clear any stale tokens from a previous session before the new login —
      // prevents the auto-login from racing with an old expired token.
      try { localStorage.removeItem('inspirotracker_token'); } catch {}

      const res = await fetch(`${API_URL}/api/auth/setup-account/${tokenParam}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password })
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.detail || 'Failed to set up account');
      }
      if (!data.token || !data.user) {
        // Account was activated but auto-login payload missing — guide user to log in manually.
        toast.success('Account activated! Please log in with your new password.');
        navigate('/login');
        return;
      }
      loginWithToken(data.token, data.user);
      toast.success('Account activated! Welcome to Inspiro Tracker.');
      navigate('/dashboard');
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-900 dark:to-slate-800">
        <Loader2 className="w-8 h-8 animate-spin text-sky-500" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-900 dark:to-slate-800 p-4">
        <Card className="w-full max-w-md" data-testid="setup-error">
          <CardContent className="pt-8 pb-6 text-center">
            <AlertCircle className="w-12 h-12 mx-auto text-red-400 mb-4" />
            <h2 className="text-lg font-semibold text-slate-900 dark:text-white mb-2">Setup Link Invalid</h2>
            <p className="text-sm text-slate-500 mb-6">{error}</p>
            <Button variant="outline" onClick={() => navigate('/login')} data-testid="go-to-login-btn">
              Go to Login
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const passwordStrength = password.length >= 12 ? 'Strong' : password.length >= 8 ? 'Good' : password.length > 0 ? 'Too short' : '';
  const strengthColor = password.length >= 12 ? 'text-emerald-600' : password.length >= 8 ? 'text-sky-600' : 'text-amber-600';

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-900 dark:to-slate-800 p-4">
      <Card className="w-full max-w-md shadow-xl border-slate-200/60" data-testid="setup-account-card">
        <CardHeader className="text-center pb-2">
          <div className="w-14 h-14 rounded-2xl bg-sky-50 dark:bg-sky-900/30 flex items-center justify-center mx-auto mb-3">
            <Lock className="w-7 h-7 text-sky-600" />
          </div>
          <CardTitle className="text-xl">Set Your Password</CardTitle>
          <CardDescription className="mt-1">
            Create a password to activate your account
          </CardDescription>
        </CardHeader>
        <CardContent>
          {/* Account Info */}
          <div className="bg-slate-50 dark:bg-slate-800/50 rounded-xl p-4 mb-6 border border-slate-200/60 dark:border-slate-700">
            <div className="flex items-center gap-3 mb-2">
              <Building className="w-4 h-4 text-slate-400" />
              <span className="text-sm font-semibold text-slate-900 dark:text-white">{accountInfo?.organization_name}</span>
            </div>
            <div className="space-y-1 text-sm text-slate-500">
              <p><span className="font-medium text-slate-700 dark:text-slate-300">{accountInfo?.name}</span></p>
              <p>{accountInfo?.email}</p>
              {accountInfo?.department && <p className="text-xs text-slate-400">{accountInfo?.department} &middot; {accountInfo?.role}</p>}
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="password" className="text-sm font-medium">Password</Label>
              <div className="relative mt-1.5">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Min 8 characters"
                  className="pr-10"
                  data-testid="setup-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              {passwordStrength && (
                <p className={`text-xs mt-1 ${strengthColor}`}>
                  Strength: {passwordStrength}
                </p>
              )}
            </div>

            <div>
              <Label htmlFor="confirmPassword" className="text-sm font-medium">Confirm Password</Label>
              <Input
                id="confirmPassword"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Re-enter your password"
                className="mt-1.5"
                data-testid="setup-confirm-password-input"
              />
              {confirmPassword && password !== confirmPassword && (
                <p className="text-xs mt-1 text-red-500">Passwords do not match</p>
              )}
              {confirmPassword && password === confirmPassword && password.length >= 8 && (
                <p className="text-xs mt-1 text-emerald-600 flex items-center gap-1">
                  <CheckCircle className="w-3 h-3" /> Passwords match
                </p>
              )}
            </div>

            <Button
              type="submit"
              className="w-full mt-2"
              disabled={submitting || password.length < 8 || password !== confirmPassword}
              data-testid="setup-submit-btn"
            >
              {submitting ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  Activating...
                </>
              ) : (
                'Activate My Account'
              )}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
